/**
 * Mapea el `reason`/`error` que devuelven los dos rieles de cupón del kiosko —
 * `requestCouponApply` (motor de validación, lib/coupons/engine.ts
 * `CouponRejectReason` + los motivos de transporte propios) y el rechazo al
 * COBRAR en `/api/orders` (P1-9, mismo set que `COUPON_DEAD_ERRORS` de
 * components/pos/POSScreen.tsx) — a la clave bajo `display.coupon.reason.*`.
 *
 * Se reutiliza ESE namespace (no uno nuevo `kiosk.coupon.reason.*`) porque
 * CouponKeypad/CouponScanner ya lo consumen tal cual en la pantalla del
 * cliente del POS (components/pos/DisplayScreen.tsx, `couponReasonMessage`) —
 * mismo copy, sin duplicar 14 claves × 2 idiomas.
 *
 * Devuelve la CLAVE (no el texto ya traducido) para que el caller decida el
 * namespace/translator — mismo patrón que `classifyPaymentError`
 * (lib/kiosk/paymentErrorCopy.ts). Uso: `t(\`reason.${couponReasonKey(x)}\`)`
 * con `t = useTranslations('display.coupon')`.
 */
export type CouponReasonKey =
  | "expired"
  | "notStarted"
  | "inactive"
  | "exhausted"
  | "alreadyUsed"
  | "alreadyConsumed"
  | "minSubtotal"
  | "alcoholOnly"
  | "invalidFormat"
  | "unavailable"
  | "unauthorized"
  | "rateLimited"
  | "network"
  | "serverError"
  | "notFound"

export function couponReasonKey(reason?: string | null): CouponReasonKey {
  switch (reason) {
    // Motivos del motor (validateCouponStrict / CouponRejectReason).
    case "expired":
      return "expired"
    case "not_started":
      return "notStarted"
    case "inactive":
      return "inactive"
    case "exhausted":
      return "exhausted"
    case "already_used":
      return "alreadyUsed"
    case "already_consumed":
      return "alreadyConsumed"
    case "min_subtotal":
      return "minSubtotal"
    // A1b (TABC): carrito todo alcohol ⇒ el cupón no tiene base a la que aplicar.
    case "alcohol_only":
      return "alcoholOnly"
    case "invalid_format":
      return "invalidFormat"
    // El cupón puede estar perfecto: lo que falló es la orden, el token del
    // kiosko o la red. Decirle "revisa el código" acá lo manda a corregir
    // algo que está bien.
    case "unavailable":
    case "feature_disabled":
      return "unavailable"
    // Motivos de TRANSPORTE que devuelve requestCouponApply (no son del motor).
    case "unauthorized":
      return "unauthorized"
    case "rate_limited":
      return "rateLimited"
    case "network":
      return "network"
    case "server_error":
      return "serverError"
    // Rechazo al COBRAR en /api/orders (P1-9): el ancla ya no sirve o el
    // carrito cambió desde que se aplicó. `coupon_rejected` es el 422 de
    // mínimo no alcanzado — sin el `minSubtotal` exacto acá (el kiosko no lo
    // pide), pero "no llega al mínimo" sigue siendo honesto.
    case "coupon_not_found":
      return "notFound"
    case "coupon_exhausted":
      return "exhausted"
    case "coupon_rejected":
      return "minSubtotal"
    default:
      return "notFound"
  }
}

/**
 * A1b: el 422 `coupon_rejected` de /api/orders lleva su motivo en `reason`
 * (`min_subtotal` o `alcohol_only`). Los dos recobros (caja y kiosko) guardan el
 * `error` como motivo del aviso; para un carrito todo alcohol hay que guardar
 * `alcohol_only`, si no el aviso diría «no llega al mínimo», que es falso.
 * Cualquier otro caso devuelve el `error` tal cual (el comportamiento de main).
 */
export function orderCouponRejectionReason(error: string, reason: unknown): string {
  return error === "coupon_rejected" && reason === "alcohol_only" ? "alcohol_only" : error
}
